import { ContentAdConfigPod, SmepEvent } from '@nativewaves/nw-cloud-sdk/smep'
import { differenceInSeconds, format, fromUnixTime, getTime } from 'date-fns'

import { AdItemProps } from 'domains/Workspaces/Events/Event/Watch/AdPods'
import { formatDuration } from 'utils'

export const formatBlockDuration = (duration: number) => {
  return (
    formatDuration(
      duration,
      { seconds: true, minutes: true, hours: true, days: true },
      { suffixed: true, omitEmptyValues: true },
    ) as string[]
  ).join(' ')
}

export const formatUnixTime = (time: number) => {
  return format(fromUnixTime(time), 'HH:mm:ss')
}

const calculateSecondOfEvent = (start: number, eventStartTime: number) =>
  differenceInSeconds(new Date(start * 1000), new Date(eventStartTime * 1000))

type Block = {
  variant: AdItemProps['variant']
  start?: number
  duration?: number
  timestampLabel?: string
}

export const formatScheduleData = (event: SmepEvent, ads: ContentAdConfigPod[] = []) => {
  const eventState = event.state
  const sortedAds = [...ads].sort((a, b) => a.start - b.start)

  const eventStartTime = event.status?.start || getTime(event.startTime) / 1000
  const eventEndTime =
    event.status && event.status.ended
      ? event.status.start + event.status.duration
      : event.endTime
        ? getTime(event.endTime) / 1000
        : undefined

  const formatTimestamp = (start: number) => {
    const seconds = calculateSecondOfEvent(start, eventStartTime)
    if (seconds === currentSecond) return undefined

    const isNegative = seconds < 0

    const absSeconds = Math.abs(seconds)
    const hours = Math.floor(absSeconds / 3600)
    const mins = Math.floor((absSeconds % 3600) / 60)
    const secs = Math.abs(seconds % 60)

    currentSecond = seconds

    return `${isNegative ? '-' : ''}${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  let cursor = eventStartTime
  let currentSecond: null | number = null

  const blocks: Block[] = [
    {
      variant: 'eventStart',
      start: cursor,
    },
  ]

  const beforeEventBlocks: number[] = []
  const afterEventBlocks: number[] = []

  sortedAds.forEach((ad) => {
    if (eventStartTime > ad.start + ad.duration) {
      beforeEventBlocks.push(1)
      return
    }
    if (eventEndTime && ad.start > eventEndTime) {
      afterEventBlocks.push(1)
      return
    }

    if (cursor < ad.start) {
      const duration = ad.start - cursor
      blocks.push({
        variant: 'content',
        start: cursor,
        duration,
        timestampLabel: formatTimestamp(cursor),
      })
    }

    blocks.push({
      variant: 'ad',
      start: ad.start,
      duration: ad.duration,
      timestampLabel: formatTimestamp(ad.start),
    })

    cursor = ad.start + ad.duration
  })

  if (!eventEndTime) {
    blocks.push({
      variant: 'content',
      start: cursor,
    })
  } else if (cursor < eventEndTime) {
    blocks.push({
      variant: 'content',
      start: cursor,
      duration: eventEndTime - cursor,
      timestampLabel: formatTimestamp(cursor),
    })
  }

  if (eventState === 'Ended') {
    blocks.push({
      variant: 'eventEnd',
      start: eventEndTime,
    })
  }

  return {
    blocks,
    beforeEventBlocks,
    afterEventBlocks,
    eventStartTime,
  }
}
